import { ISectionLogger } from "@multiweb/logging";
import { FAKE_COMPONENT_VALUE_LOC_KEY } from "@multiweb/reactComponents";
import { IAttrValue, OWDataType } from "@multiweb/types/contracts";
import { StringUtils } from "Global/Base/Utils/StringUtils";
import { AttrValueReader } from "OW.Remoting/Remoting/AttrValueReader";
import { AttributeDescription } from "OW.Remoting/Remoting/Cache/ClassDescriptionCache/Interfaces";
import { Localization } from "OW.Remoting/Remoting/Localization";
import { ITextBuilder } from "../../../../Global/Base/Builders/ITextBuilder";
import { Event } from "../../../../Global/Base/Events";
import { DateUtils } from "../../../../Global/Base/Utils/DateUtils";
import { NumberUtils } from "../../../../Global/Base/Utils/NumberUtils";
import { Enum } from "../../../../OW.Remoting/Remoting/DataTypes/Enums";
import { NullOWObject, OWObject } from "../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { FakeComponentValueState } from "../Controls/ControlEnums";
import { ControlWithLoggerParams, IAttributeControl, IHasFrameContext, IMultiEdit } from "../Controls/ControlInterfaces";
import { ControlWithBackground } from "../Controls/ControlWithBackground";
import { EditableControl } from "../Controls/EditableControl";
import { FrameContext } from "../UIManagerInterfaces";

export class DataLabel extends ControlWithBackground implements IAttributeControl, IMultiEdit, IHasFrameContext {
    //#region Fields
    private _attrName: string;
    private _dataType: DataLabelDataType = DataLabelDataType.other;
    private readonly _logger: ISectionLogger;

    private _description: AttributeDescription | null = null;
    private _innerElement: HTMLLabelElement;
    private _originalDisplayFormat: string;
    private _displayFormat: string;
    private __storedPostponedValue: any = undefined;
    public readonly valueChanged: Event<EditableControl, any> = undefined;
    public isValueLoaded: boolean;
    public frameContext: FrameContext | undefined = undefined;
    /** Indikátor aktivnosti hromadné editace. */
    private _multiEditActive: boolean = false;
    /** Indikátor, zda při hromadné editaci byla nastavena hodnota. U DataLabel nemůže nastavení hodnoty způsobit uživatel přímo na této komponentě,
     * může ale editovat zobrazenou hodnotu v komponentě jiné (DataLabel zobrazuje hodnotu stejného atributu, který se editoval v jiné komponentě). */
    private __touched: boolean = false;
    /**
     * Řeší problém při změně výběru. Při setování controlValue je potřeba rozlišit, jestli hodnota null přišla v reakci na stisk backspace nebo to je načtená hodnota z db a podle toho vyrenderovat fake kompnentu.
     */
    private _valueState: FakeComponentValueState;
    /** Uchovává hodnotu, která je zobrazovaná v případě, že není aktivní hromadná editace. */
    private _displayedValue: string;

    //#endregion Fields

    //#region Properties
    /**
     * Uchovaná hodnota, o jejíž nastavení byl proveden pokus.
     * Nebyla ale k dispozici description koncového atributu - pravděpodobně kvůli prázdnému neexistujicímu atributu v attrName.
     * Až je hodnota komponentě úspěšně nastavena, odsud se vyprázdní.
     */
    private get _storedPostponedValue(): any {
        return this.__storedPostponedValue;
    }
    private set _storedPostponedValue(value: any) {
        if (value == null) {
            this.hideLoader();
        } else if (this.__storedPostponedValue !== value) {
            this.showLoader();
        }

        this.__storedPostponedValue = value;
    }

    public get attrName(): string {
        return this._attrName;
    }

    public set attrName(value: string) {
        this._attrName = value;
    }

    public set controlValue(value: any) {
        this._setControlValue(value) // Async metoda!
            .then(() => {
                if (value)
                    this._valueState = FakeComponentValueState.userChanged;
                //komponentu je potřeba nastavit jako změněnou pokud uživatel smazal hodnotu přes fake nebo nastavil konkrétní hodnotu
                if (this._valueState !== FakeComponentValueState.notSet && !this._touched)
                    this._touched = true;
            });
    }

    public setInitialControlValue(value: any): void {
        // Pozn. setInitialControlValue byla zavedena primárně pro komponenty přepsané do Reactu.
        // U původních/nereactích komponent je záměrně ponecháno původní chování.
        this.controlValue = value;
    }

    public set displayedValue(v: ITextBuilder) {
        this._displayedValue = v?.toString();
        if (!this._multiEditActive) {
            this._innerElement.textContent = this._displayedValue;
        }
    }

    public get displayedValue(): ITextBuilder {
        if (this._multiEditActive) {
            return null;
        }
        return this._displayedValue;
    }

    public get touched(): boolean {
        return this._touched;
    }

    //#endregion Properties

    //#region Constructors
    constructor({ logger, ...otherParams }: ControlWithLoggerParams) {
        super(otherParams);
        this._logger = logger;

        this.element.classList.add("DataLabel", "loader-enabled");
        this._innerElement = document.createElement("label");
        this.element.appendChild(this._innerElement);

        this._originalDisplayFormat = this.getDataAttr("displayformat");
        this._displayFormat = this._originalDisplayFormat;

        this._setCaptionReference(this._innerElement);
    }
    //#endregion Constructors

    //#region Methods
    protected override async _setActualObject(value: OWObject | null): Promise<void> {
        if (value === this.actualObject) {
            return;
        }
        super._setActualObject(value);

        // Při clearu actualObjectu zahodíme dříve načtenou descriptionu
        if (value == null) {
            this._description = null;
            this.displayedValue = "";

            return;
        }

        await this._tryGetDescription();

        await this._trySetPostponedValue();
    }

    private async _tryGetDescription(): Promise<boolean> {
        // AttrName se rozdělí na jednotlivé vazební atributy
        const attributeParts = this.attrName.split(".");

        let temporaryObject: OWObject | null = this.actualObject;
        if (temporaryObject === null) { // pokud není k dispozici actualObject, není z čeho description získat
            return false;
        }

        let nextAttribute: string | null = null;

        if (attributeParts.length > 1) {
            let i: number;

            // Postupně se getují hodnoty vazebních atributů
            for (i = 0; i < attributeParts.length - 1; ++i) {
                nextAttribute = attributeParts[i];
                let attrValueRaw: IAttrValue;
                try {
                    attrValueRaw = await temporaryObject.getValue(nextAttribute);
                } catch (exception) {
                    return false; // Atribut se nepodařilo získat, končíme
                }
                const attrValue = AttrValueReader.read(attrValueRaw);
                if (attrValue == null || !(attrValue instanceof OWObject) || attrValue instanceof NullOWObject) {
                    return false; // Hodnota atributu je prázdná, končíme
                }

                temporaryObject = attrValue;
            }

            nextAttribute = attributeParts[i];
        } else {
            nextAttribute = attributeParts[0];
        }

        // Podařilo se získat předposlední hodnotu atributu, na ní získáme description posledního atributu
        this._description = await temporaryObject.getAttributeMetadata(nextAttribute);

        return this._description != null;
    }

    private async _setControlValue(value: any, skipDescriptionGetting: boolean = false): Promise<boolean> {
        if (!skipDescriptionGetting) {
            await this._tryGetDescription();
        }

        if (!this._description) {
            this._storedPostponedValue = value;
            return false;
        }

        const dataType = this._description.type;

        if (NumberUtils.DATE_TYPES.has(dataType)) {
            this._dataType = DataLabelDataType.date;
        } else if (NumberUtils.NUMBER_TYPES.has(dataType)) {
            this._dataType = DataLabelDataType.number;
        } else if (dataType.startsWith(OWDataType.boolean)) {
            this._dataType = DataLabelDataType.boolean;
        } else if (dataType.startsWith(OWDataType.enum)) {
            this._dataType = DataLabelDataType.Enum;
        } else if (dataType.startsWith(OWDataType.htmlText)) {
            this._dataType = DataLabelDataType.htmltext;
        }

        let controlValue = value?.toString();

        try {
            if (!this._originalDisplayFormat) // Pokud není nastaven konkrétní DF
                this._displayFormat = NumberUtils.getDefaultFormat(dataType); // tak se nastaví na základní datového typu

            switch (this._dataType) {
                case DataLabelDataType.date:
                    controlValue = value ? DateUtils.getDateToStringFunction(this._displayFormat).format(value) : "";
                    break;
                case DataLabelDataType.number:
                    controlValue = value || value === 0 ? NumberUtils.getNumberToStringFunction(this._displayFormat).format(value) : "";
                    break;
                case DataLabelDataType.boolean:
                    if (value != null) {
                        if (value)
                            controlValue = await Localization.getTextPromise("GUI-146838"); // Ano
                        else
                            controlValue = await Localization.getTextPromise("GUI-146840"); // Ne
                    }
                    break;
                case DataLabelDataType.Enum:
                    if (value) {
                        controlValue = await (value as Enum).getLocalizedName();
                    }
                    break;
                case DataLabelDataType.htmltext:
                    if (value) {
                        controlValue = StringUtils.stripHTMLFromText(value);
                    }
                    break;
            }
            this.displayedValue = controlValue ?? "";
        } catch (e) {
            this._logger.error(e);

            return false;
        }

        return true;
    }

    /**
     * Pokusí se nastavit odloženou uchovanou hodnotu
     * @returns True, pokud se podařilo hodnotu nastavit. Jinak False
     */
    private async _trySetPostponedValue(): Promise<boolean> {
        if (this._storedPostponedValue) {
            // Parametr skipDescriptionGetting předáme vždy true, protože metoda _trySetPostponedValue je volána jen z jednoho místa, na kterém se getování descriptiony už volá. Aby se tedy nevolalo hned zbytečně znovu.
            const valueSuccessfullySet = await this._setControlValue(this._storedPostponedValue, true);
            if (valueSuccessfullySet) {
                this._storedPostponedValue = undefined;
            }
            return valueSuccessfullySet;
        }

        return false;
    }

    public get multiEditActive(): boolean {
        return this._multiEditActive;
    }

    public set multiEditActive(value: boolean) {
        this._multiEditActive = value;
        this._touched = false;
        this. _valueState = FakeComponentValueState.notSet;
        if (this._multiEditActive) {
            this.activateMultiEditView();
        }
        if (!this._multiEditActive) {
            this.deactivateMultiEditView();
            this._touched = false;
        }
    }

    /**
     * Aktivuje zobrazení pro hromadnou editaci, kdy je zobrazen text "Více hodnot" a zároveň se nastaví vysvětlující popisek.
     */
    private activateMultiEditView(): void {
        Localization.getLocalizationItem(FAKE_COMPONENT_VALUE_LOC_KEY).then((localizationItem) => {
            this._innerElement.textContent = localizationItem?.primaryText;
            this._innerElement.setAttribute("title", localizationItem?.secondaryText);
        });
    }

    /**
     * Deaktivuje zobrazení pro hromadnou editaci. Zobrazí původní hodnotu a odebere vysvětlující popisek.
     */
    private deactivateMultiEditView(): void {
        this._innerElement.textContent = this._displayedValue;
        this._innerElement.removeAttribute("title");
    }

    /**
     * Nastaví příznak, že byla nastavena hodnota v průběhu aktivní hromadné editace.
     * Pokud je nastaveno na true v průběhu hromadné editace, zobrazí reálnou hodnotu.
     * @param value Hodnota k nastavení. Hodnota true se nastaví pouze pokud je aktivní hromadná editace.
     */
    private set _touched(value: boolean) {
        this.__touched = this.multiEditActive && value;
        if (this._touched) {
            this.deactivateMultiEditView();
        }
    }

    /**
     * Popis viz {@link __touched}.
     */
    private get _touched(): boolean {
        return this.__touched;
    }

    //#endregion Methods
}

export enum DataLabelDataType {
    Enum,
    date,
    number,
    boolean,
    htmltext,
    other,
}
